import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ExternalLink } from "lucide-react";

const SERIES = ["Belfast Murders", "Galway Murders", "Others"];

const SERIES_DESC = {
  "Belfast Murders": "Follow Detective Inspector Wilson and his team as they investigate crimes of corruption, murder, and political intrigue across twelve gripping novels set in the heart of Belfast, Northern Ireland.",
  "Galway Murders": "DI Fiona Madden navigates the treacherous landscapes of Connemara and Galway in this nine-book Irish detective series, uncovering dark secrets hidden beneath Ireland's most beautiful and dangerous coastlines.",
  "Others": "Derek Fee's standalone crime novels span international thrillers, psychological dramas, and supernatural mysteries — each delivering masterful plotting and unforgettable characters."
};

function BookCard({ book }) {
  return (
    <a
      href={book.amazon_link}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`book-card-${book.id}`}
      className="book-card block group"
    >
      <div className="relative aspect-[2/3]">
        <img
          src={book.cover_url}
          alt={`${book.title} by Derek Fee - ${book.series} crime thriller novel`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="book-overlay absolute inset-0 bg-black/60 flex items-center justify-center">
          <span className="flex items-center gap-2 text-white text-sm uppercase tracking-widest">
            View on Amazon <ExternalLink className="h-4 w-4" />
          </span>
        </div>
      </div>
      <div className="p-3 bg-zinc-950">
        <h3 className="font-display text-white text-sm font-bold truncate">{book.title}</h3>
      </div>
    </a>
  );
}

export default function BooksSection({ books }) {
  return (
    <section id="books" data-testid="books-section" className="py-20 md:py-32">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-rose-700 uppercase tracking-[0.3em] text-sm font-bold">
          Bibliography
        </p>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3 tracking-tight">
          Books
        </h2>
        <p className="text-zinc-400 text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
          Browse all of Derek Fee's Irish crime thriller novels below. His detective fiction
          spans multiple styles including police procedural, political thriller, action, and
          supernatural — set across Belfast, Galway, Connemara, and beyond.
        </p>

        <Tabs defaultValue="Belfast Murders" className="mt-12">
          <TabsList className="bg-transparent border-b border-white/10 w-full justify-start gap-4 md:gap-8 rounded-none p-0 h-auto flex-wrap">
            {SERIES.map(s => (
              <TabsTrigger
                key={s}
                value={s}
                data-testid={`tab-${s.toLowerCase().replace(/\s/g, "-")}`}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-rose-700 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none text-zinc-500 text-base md:text-lg font-display px-0 py-4 hover:text-zinc-300 transition-colors bg-transparent"
              >
                {s}
              </TabsTrigger>
            ))}
          </TabsList>
          {SERIES.map(s => (
            <TabsContent key={s} value={s} className="mt-8">
              {SERIES_DESC[s] && (
                <p className="text-zinc-500 text-sm mb-6 max-w-3xl leading-relaxed">{SERIES_DESC[s]}</p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {books
                  .filter(b => b.series === s)
                  .sort((a, b) => a.order - b.order)
                  .map(book => (
                    <BookCard key={book.id} book={book} />
                  ))}
              </div>
              {books.filter(b => b.series === s).length === 0 && (
                <p className="text-zinc-600 text-center py-16">No books in this series yet.</p>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
